// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { AccountCurrency } from '@shared/data-access/types/brokerage-types';

/**
 * Engine API — illiquid assets
 *
 * @remarks
 * **Type Organization:**
 *
 * - **Base Types:** {@link ListIlliquidAssetsParams},
 *   {@link DeleteIlliquidAssetParams}, subcategory enums
 * - **Schema Types:** {@link IlliquidAssetResponse},
 *   {@link IlliquidAssetsListResponse}, processed {@link IlliquidAsset},
 *   {@link IlliquidAssetsList}
 * - **Endpoint Types:** discriminated {@link CreateIlliquidAssetRequestBody} variants
 */
/** Top-level category for an illiquid asset row and create payloads. */
export enum IlliquidAssetType {
  RealEstate = 'real_estate',
  Company = 'company',
  PeVc = 'pe_vc',
  Tangible = 'tangible',
  OtherIlliquid = 'other_illiquid',
}

/**
 * Supported ISO 4217 currency codes for illiquid asset monetary fields.
 *
 * @see https://www.iso.org/iso-4217-currency-codes.html
 */
export enum IlliquidAssetCurrency {
  USD = 'USD',
  EUR = 'EUR',
  JPY = 'JPY',
  GBP = 'GBP',
  CNY = 'CNY',
  CNH = 'CNH',
  AUD = 'AUD',
  CAD = 'CAD',
  CHF = 'CHF',
  HKD = 'HKD',
  NZD = 'NZD',
}

/** Subcategory when {@link IlliquidAssetType} is real estate. */
export enum RealEstateSubcategory {
  OwnerOccupiedHome = 'owner_occupied_home',
  RentalProperty = 'rental_property',
  SecondResidence = 'second_residence',
  CommercialPremises = 'commercial_premises',
  Office = 'office',
  IndustrialLogistics = 'industrial_logistics',
  HotelHostel = 'hotel_hostel',
  UrbanLand = 'urban_land',
  RusticAgriculturalLand = 'rustic_agricultural_land',
  RusticFarm = 'rustic_farm',
  RealEstateDevelopment = 'real_estate_development',
  OtherRealEstate = 'other_real_estate',
}

/** Subcategory when {@link IlliquidAssetType} is a private company position. */
export enum CompanySubcategory {
  FamilyBusiness = 'family_business',
  PrivateCompany = 'private_company',
  Holding = 'holding',
  RealEstateCompany = 'real_estate_company',
  OwnBusiness = 'own_business',
  Other = 'other',
}

/** Subcategory for tangible and collectible sleeves. */
export enum TangibleSubcategory {
  Art = 'art',
  Jewelry = 'jewelry',
  Watches = 'watches',
  ClassicCars = 'classic_cars',
  Collectibles = 'collectibles',
  Other = 'other',
}

/**
 * Subcategory for other illiquid buckets (rights, concessions, blocked
 * instruments).
 */
export enum OtherIlliquidSubcategory {
  RoyaltiesRights = 'royalties_rights',
  Concessions = 'concessions',
  CryptoTokensBlocked = 'crypto_tokens_blocked',
  Other = 'other',
}

/** Subcategory for private equity and venture-style sleeves. */
export enum PeVcSubcategory {
  PrivateEquityFund = 'private_equity_fund',
  VentureCapitalFund = 'venture_capital_fund',
  DirectInvestmentStartup = 'direct_investment_startup',
  ClubDeal = 'club_deal',
  Spv = 'spv',
  Other = 'other',
}

/** PE/VC investment structure label where applicable. */
export enum PeVcInvestmentType {
  PeFund = 'pe_fund',
  VcFund = 'vc_fund',
  Startup = 'startup',
  ClubDeal = 'club_deal',
  Spv = 'spv',
  Other = 'other',
}

/** Union of illiquid subcategory enums allowed on a stored asset row. */
export type IlliquidAssetApiSubcategory =
  | RealEstateSubcategory
  | CompanySubcategory
  | TangibleSubcategory
  | OtherIlliquidSubcategory
  | PeVcSubcategory;

/** Financial event kinds stored on an illiquid asset. */
export enum IlliquidAssetEventType {
  Acquisition = 'acquisition',
  CapitalCall = 'capital_call',
  Distribution = 'distribution',
  Valuation = 'valuation',
}

/** Subtype when {@link IlliquidAssetEventType.CapitalCall} is selected. */
export enum CapitalCallSubtype {
  Executed = 'executed',
  Planned = 'planned',
}

/** Subtype when {@link IlliquidAssetEventType.Distribution} is selected. */
export enum DistributionSubtype {
  Liquidity = 'liquidity',
  PartialSale = 'partial_sale',
  FullSale = 'full_sale',
  Dividend = 'dividend',
}

/** Allowed subtype per event type (null for acquisition and valuation). */
export type IlliquidAssetEventSubtypeByType = {
  [IlliquidAssetEventType.Acquisition]: null;
  [IlliquidAssetEventType.Valuation]: null;
  [IlliquidAssetEventType.CapitalCall]: CapitalCallSubtype;
  [IlliquidAssetEventType.Distribution]: DistributionSubtype;
};

/** Union of all event subtype values returned by the engine. */
export type IlliquidAssetEventSubtype =
  | CapitalCallSubtype
  | DistributionSubtype
  | null;

/** Shared wire fields for every illiquid asset event row. */
export interface IlliquidAssetEventResponseBase {
  id: number;

  illiquid_asset_id: number;

  /**
   * Business date of the event (`YYYY-MM-DD`). Multiple events may share the same
   * day. Form create flows collect date only.
   */
  event_date: string;

  amount?: number | null;

  currency?: string | null;

  description?: string | null;

  /**
   * Server timestamp when the event was created (`YYYY-MM-DDTHH:mm:ssZ`).
   *
   * @remarks
   * Future value-evolution charts must plot / sort / dedupe series points by
   * {@link created_at} (not {@link event_date}) so same-day valuations do not collide
   * in chart libraries that require unique ascending times.
   */
  created_at: string;

  updated_at: string;
}

/** Wire row for an acquisition event. */
export interface AcquisitionIlliquidAssetEventResponse
  extends IlliquidAssetEventResponseBase {
  event_type: IlliquidAssetEventType.Acquisition;

  subtype: null;
}

/** Wire row for a capital call event. */
export interface CapitalCallIlliquidAssetEventResponse
  extends IlliquidAssetEventResponseBase {
  event_type: IlliquidAssetEventType.CapitalCall;

  subtype: CapitalCallSubtype;
}

/** Wire row for a distribution event. */
export interface DistributionIlliquidAssetEventResponse
  extends IlliquidAssetEventResponseBase {
  event_type: IlliquidAssetEventType.Distribution;

  subtype: DistributionSubtype;
}

/** Wire row for a valuation event. */
export interface ValuationIlliquidAssetEventResponse
  extends IlliquidAssetEventResponseBase {
  event_type: IlliquidAssetEventType.Valuation;

  subtype: null;
}

/** Discriminated union of event rows returned by the engine. */
export type IlliquidAssetEventResponse =
  | AcquisitionIlliquidAssetEventResponse
  | CapitalCallIlliquidAssetEventResponse
  | DistributionIlliquidAssetEventResponse
  | ValuationIlliquidAssetEventResponse;

/** Client-side event row. Amounts stay in the event's own currency (no FX). */
export type IlliquidAssetEvent = IlliquidAssetEventResponse;

/** Shared create fields for every illiquid asset event payload. */
export interface IlliquidAssetEventCreateBase {
  event_date: string;

  amount: number;

  currency: IlliquidAssetCurrency | string;

  description?: string | null;
}

/** Create body for an acquisition event. */
export interface AcquisitionEventCreateBody extends IlliquidAssetEventCreateBase {
  event_type: IlliquidAssetEventType.Acquisition;

  subtype?: null;
}

/** Create body for a capital call event. */
export interface CapitalCallEventCreateBody extends IlliquidAssetEventCreateBase {
  event_type: IlliquidAssetEventType.CapitalCall;

  subtype: CapitalCallSubtype;
}

/** Create body for a distribution event. */
export interface DistributionEventCreateBody extends IlliquidAssetEventCreateBase {
  event_type: IlliquidAssetEventType.Distribution;

  subtype: DistributionSubtype;
}

/** Create body for a valuation event. */
export interface ValuationEventCreateBody extends IlliquidAssetEventCreateBase {
  event_type: IlliquidAssetEventType.Valuation;

  subtype?: null;
}

/** Discriminated union of event create payloads. */
export type CreateIlliquidAssetEventRequestBody =
  | AcquisitionEventCreateBody
  | CapitalCallEventCreateBody
  | DistributionEventCreateBody
  | ValuationEventCreateBody;

/**
 * Original monetary amounts in the asset’s native currency, before account-currency
 * conversion in the processed illiquids API layer.
 */
export interface IlliquidAssetLocalCurrencyValues {
  /** Native ISO 4217 code for stored amounts. */
  currency: IlliquidAssetCurrency;

  /** Latest estimated value in {@link currency}. */
  current_valuation?: number | null;

  /** Acquisition amount in {@link currency} when applicable. */
  acquisition_value?: number | null;

  /** PE/VC: total commitment in {@link currency} when applicable. */
  total_commitment?: number | null;
}

/**
 * Illiquid asset as returned by the engine (list and create responses).
 *
 * @remarks
 * Amounts use `currency`. `ownership_pct` is a fraction in `0`–`1`. PE-specific
 * fields apply when {@link IlliquidAssetType.PeVc} is selected.
 */
export interface IlliquidAssetResponse {
  /** Discriminator and reporting category. */
  category: IlliquidAssetType;

  /** Human-readable name. */
  name: string;

  /** Fine-grained classification within `category`. */
  subcategory: IlliquidAssetApiSubcategory;

  /** Optional long-form description. */
  description?: string | null;

  /** ISO 4217 currency for monetary fields (native to the stored asset). */
  currency: IlliquidAssetCurrency;

  /** Latest estimated value in `currency`. */
  current_valuation?: number | null;

  /** ISO date string for `current_valuation` when set. */
  valuation_date?: string | null;

  /** Primary key. */
  id: number;

  /** Owning client record id in Citec. */
  citec_user_clients_id: number;

  /** Acquisition date when applicable. */
  acquisition_date?: string | null;

  /** Acquisition amount in `currency` when applicable. */
  acquisition_value?: number | null;

  /** Fractional ownership `0`–`1` when applicable. */
  ownership_pct?: number | null;

  /** PE/VC: fund manager name when applicable. */
  fund_manager?: string | null;

  /** PE/VC: total commitment in `currency` when applicable. */
  total_commitment?: number | null;

  /** Record creation time (ISO date-time). */
  created_at: string;

  /** Last update time (ISO date-time). */
  updated_at: string;

  /** Chronological financial events when included by the engine. */
  events?: IlliquidAssetEventResponse[];

  /** IBANs of client bank products linked to this asset. */
  ibans: string[];
}

/**
 * Illiquid asset after client-side conversion to the user’s account currency.
 *
 * @remarks
 * Top-level monetary fields match {@link AccountCurrency}; originals are under
 * {@link local_currency_values}.
 */
export interface IlliquidAsset
  extends Omit<IlliquidAssetResponse, 'currency' | 'events'> {
  currency: AccountCurrency;

  /** Native monetary amounts before FX conversion. */
  local_currency_values: IlliquidAssetLocalCurrencyValues;

  /** Financial events in each event's own currency (not FX-converted). */
  events?: IlliquidAssetEvent[];
}

/** List payload for illiquid assets for the active client (engine). */
export interface IlliquidAssetsListResponse {
  /** Asset rows. */
  assets: IlliquidAssetResponse[];
}

/** List payload from the processed illiquids API (converted amounts). */
export interface IlliquidAssetsList {
  /** Asset rows. */
  assets: IlliquidAsset[];
}

/** Create body when {@link IlliquidAssetType.RealEstate} is selected. */
export interface IlliquidRealEstateCreateBody {
  category: IlliquidAssetType.RealEstate;
  name: string;
  subcategory: RealEstateSubcategory;
  description?: string | null;
  currency: IlliquidAssetCurrency;
  current_valuation?: number | null;
  valuation_date?: string | null;
  acquisition_date: string;
  acquisition_value: number;
  ownership_pct: number;
  /** Optional financial events created with the asset. */
  events?: CreateIlliquidAssetEventRequestBody[];
  /** Optional IBANs of bank products to link on create. */
  ibans?: string[];
}

/** Create body when {@link IlliquidAssetType.Company} is selected. */
export interface IlliquidCompanyCreateBody {
  category: IlliquidAssetType.Company;
  name: string;
  subcategory: CompanySubcategory;
  description?: string | null;
  currency: IlliquidAssetCurrency;
  current_valuation?: number | null;
  valuation_date?: string | null;
  acquisition_date: string;
  acquisition_value: number;
  ownership_pct: number;
  /** Optional financial events created with the asset. */
  events?: CreateIlliquidAssetEventRequestBody[];
  /** Optional IBANs of bank products to link on create. */
  ibans?: string[];
}

/** Create body when {@link IlliquidAssetType.Tangible} is selected. */
export interface IlliquidTangibleCreateBody {
  category: IlliquidAssetType.Tangible;
  name: string;
  subcategory: TangibleSubcategory;
  description?: string | null;
  currency: IlliquidAssetCurrency;
  current_valuation?: number | null;
  valuation_date?: string | null;
  acquisition_date: string;
  acquisition_value: number;
  ownership_pct: number;
  /** Optional financial events created with the asset. */
  events?: CreateIlliquidAssetEventRequestBody[];
  /** Optional IBANs of bank products to link on create. */
  ibans?: string[];
}

/** Create body when {@link IlliquidAssetType.OtherIlliquid} is selected. */
export interface IlliquidOtherIlliquidCreateBody {
  category: IlliquidAssetType.OtherIlliquid;
  name: string;
  subcategory: OtherIlliquidSubcategory;
  description?: string | null;
  currency: IlliquidAssetCurrency;
  current_valuation?: number | null;
  valuation_date?: string | null;
  acquisition_date: string;
  acquisition_value: number;
  ownership_pct: number;
  /** Optional financial events created with the asset. */
  events?: CreateIlliquidAssetEventRequestBody[];
  /** Optional IBANs of bank products to link on create. */
  ibans?: string[];
}

/** Create body when {@link IlliquidAssetType.PeVc} is selected. */
export interface IlliquidPeVcCreateBody {
  category: IlliquidAssetType.PeVc;
  name: string;
  subcategory: PeVcSubcategory;
  description?: string | null;
  currency: IlliquidAssetCurrency;
  current_valuation?: number | null;
  valuation_date?: string | null;
  fund_manager: string;
  total_commitment: number;
  /** Optional financial events created with the asset. */
  events?: CreateIlliquidAssetEventRequestBody[];
  /** Optional IBANs of bank products to link on create. */
  ibans?: string[];
}

/**
 * Discriminated union of create payloads; choose the variant that matches
 * `category`.
 */
export type CreateIlliquidAssetRequestBody =
  | IlliquidRealEstateCreateBody
  | IlliquidCompanyCreateBody
  | IlliquidTangibleCreateBody
  | IlliquidOtherIlliquidCreateBody
  | IlliquidPeVcCreateBody;

/** Query parameters for listing illiquid assets and optional scope on create. */
export interface ListIlliquidAssetsParams {
  /**
   * When set, scopes the request to that client; behavior follows engine auth rules
   * when omitted.
   */
  client_id?: number | null;
}

/**
 * Processed list query: engine {@link ListIlliquidAssetsParams} plus display
 * currency for FX conversion (processed illiquids API).
 */
export type ListIlliquidAssetsQueryParams = ListIlliquidAssetsParams & {
  currency: AccountCurrency;
};

/**
 * Processed create mutation: request body, optional client scope, and display
 * currency for FX conversion on the response.
 */
export type CreateIlliquidAssetMutationParams = ListIlliquidAssetsParams & {
  body: CreateIlliquidAssetRequestBody;
  currency: AccountCurrency;
};

/** Parameters for deleting an illiquid asset. */
export interface DeleteIlliquidAssetParams {
  /** Asset id from {@link IlliquidAsset.id}. */
  asset_id: number;

  /**
   * Optional client scope, same semantics as
   * {@link ListIlliquidAssetsParams.client_id}.
   */
  client_id?: number | null;
}

/** PATCH body for `PATCH /citec/illiquid-assets/{asset_id}/`. */
export interface IlliquidAssetUpdate {
  name?: string | null;

  subcategory?: string | null;

  description?: string | null;

  currency?: string | null;

  current_valuation?: number | null;

  valuation_date?: string | null;

  acquisition_date?: string | null;

  acquisition_value?: number | null;

  ownership_pct?: number | null;

  fund_manager?: string | null;

  total_commitment?: number | null;

  /** Full replacement list of linked IBANs (link/unlink = send new array). */
  ibans?: string[] | null;
}

/** Parameters for updating an illiquid asset (raw engine). */
export interface UpdateIlliquidAssetParams {
  asset_id: number;

  client_id?: number | null;

  body: IlliquidAssetUpdate;
}

/**
 * Processed update mutation: engine params plus display currency for FX conversion
 * on the response.
 */
export interface UpdateIlliquidAssetMutationParams
  extends UpdateIlliquidAssetParams {
  currency: AccountCurrency;
}

/** Parameters for creating an illiquid asset event. */
export interface CreateIlliquidAssetEventParams {
  asset_id: number;

  body: CreateIlliquidAssetEventRequestBody;

  client_id?: number | null;
}

/** Parameters for deleting an illiquid asset event. */
export interface DeleteIlliquidAssetEventParams {
  asset_id: number;

  event_id: number;

  client_id?: number | null;
}
